import React from 'react';
import { Box, Typography, useTheme, Tooltip } from "@mui/material";
import { tokens } from "../../theme";

const MatrixHeatmap = ({ data, title }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    if (!data || !data.data || !data.columns || !data.index) {
        // Fallback for empty or invalid data
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="300px" border={`1px dashed ${colors.grey[500]}`} borderRadius="8px">
                <Typography color={colors.grey[300]} variant="h5" mb={1}>
                    {!data ? "Global Matrix Not Found" : "Invalid Matrix Structure"}
                </Typography>
                <Typography color={colors.grey[500]} variant="body2">
                    Please ensure the database has a &apos;GLOBAL&apos; scope entry for the selected module.
                </Typography>
            </Box>
        );
    }

    const headers = data.columns;
    const rows = data.index;
    const matrix = data.data;

    // Determine min/max for color scaling
    let minVal = 0;
    let maxVal = 0;

    matrix.forEach(row => {
        row.forEach(val => {
            if (val < minVal) minVal = val;
            if (val > maxVal) maxVal = val;
        });
    });

    // Color scale logic (YlGnBu style - Scientific)
    const getBackgroundColor = (value) => {
        let intensity = 0;
        if (maxVal > 0) {
            intensity = Math.max(0, value / (maxVal * 1.0));
        }

        // Multi-point gradient interpolation
        // 0.0: #ffffd9 (255, 255, 217) - Very Light Yellow
        // 0.2: #edf8b1 (237, 248, 177)
        // 0.4: #c7e9b4 (199, 233, 180) - Light Green
        // 0.6: #7fcdbb (127, 205, 187) - Medium Teal
        // 0.8: #2c7fb8 (44, 127, 184) - Blue
        // 1.0: #253494 (37, 52, 148) - Dark Blue

        // Simplified 3-step interpolation
        if (intensity < 0.5) {
            // Yellow (255, 255, 217) to Teal (127, 205, 187)
            const t = intensity * 2; // 0 to 1
            const r = Math.round(255 + (127 - 255) * t);
            const g = Math.round(255 + (205 - 255) * t);
            const b = Math.round(217 + (187 - 217) * t);
            return `rgb(${r}, ${g}, ${b})`;
        } else {
            // Teal (127, 205, 187) to Dark Blue (37, 52, 148)
            const t = (intensity - 0.5) * 2; // 0 to 1
            const r = Math.round(127 + (37 - 127) * t);
            const g = Math.round(205 + (52 - 205) * t);
            const b = Math.round(187 + (148 - 187) * t);
            return `rgb(${r}, ${g}, ${b})`;
        }
    };

    const getTextColor = (value) => {
        // Dark text for low intensity (yellows/greens), White for high (blues)
        let intensity = 0;
        if (maxVal > 0) {
            intensity = Math.max(0, value / (maxVal * 1.0));
        }
        return (intensity > 0.6) ? '#fff' : '#000';
    };

    return (
        <Box sx={{ width: '100%', overflowX: 'auto', p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {title && (
                <Typography variant="h6" fontWeight="bold" align="center" mb={1} color={colors.grey[100]}>
                    {title}
                </Typography>
            )}
            <table style={{
                borderCollapse: 'separate',
                borderSpacing: '2px',
                width: 'auto',
                maxWidth: '100%',
                tableLayout: 'fixed'
            }}>
                <thead>
                    <tr>
                        <th style={{ width: '80px' }}>
                            <Typography variant="caption" color={colors.grey[400]} sx={{ fontSize: '0.65rem' }}>FROM \ TO</Typography>
                        </th>
                        {headers.map((header, i) => (
                            <th key={i} style={{ width: '70px', textAlign: 'center' }}>
                                <Typography variant="caption" fontWeight="bold" color={colors.grey[300]} sx={{ fontSize: '0.7rem' }}>
                                    {header.replace('_', ' ')}
                                </Typography>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((rowLabel, i) => (
                        <tr key={i}>
                            <td style={{ padding: '4px', textAlign: 'right' }}>
                                <Typography variant="caption" fontWeight="bold" color={colors.grey[300]} sx={{ fontSize: '0.7rem' }}>
                                    {rowLabel.replace('_', ' ')}
                                </Typography>
                            </td>
                            {matrix[i].map((value, j) => {
                                const diff = data.diffs ? data.diffs[i][j] : 0;
                                const hasDiff = Math.abs(diff) > 0.0001;
                                const contributions = data.breakdown ? data.breakdown[i][j] : [];

                                return (
                                    <Tooltip
                                        key={j}
                                        title={
                                            <Box textAlign="center" p={0.5}>
                                                <Typography variant="body2" fontWeight="800" fontSize="1rem" color="#fff">
                                                    {(value * 100).toFixed(2)}%
                                                </Typography>

                                                {/* Breakdown of Contributions */}
                                                {contributions.length > 0 && (
                                                    <Box mt={1} pt={1} borderTop="1px solid rgba(255,255,255,0.4)" textAlign="left">
                                                        <Typography variant="caption" display="block" color="#fff" fontWeight="bold" mb={0.5}>Impact by Filter:</Typography>
                                                        {contributions.map((c, idx) => (
                                                            <Box key={idx} display="flex" justifyContent="space-between" gap={2}>
                                                                <Typography variant="caption" color={colors.grey[200]} fontWeight="500">{c.label}:</Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    color={c.value > 0 ? '#00e676' : '#ff1744'}
                                                                    fontWeight="800"
                                                                >
                                                                    {c.value > 0 ? "+" : ""}{(c.value * 100).toFixed(2)}%
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                            </Box>
                                        }
                                        slotProps={{
                                            tooltip: {
                                                sx: {
                                                    backgroundColor: '#000',
                                                    color: '#fff',
                                                    border: '1px solid rgba(255,255,255,0.5)',
                                                    boxShadow: '0px 4px 20px rgba(0,0,0,0.8)',
                                                    fontSize: '0.8rem',
                                                    padding: '8px 12px'
                                                }
                                            },
                                            arrow: {
                                                sx: {
                                                    color: '#000',
                                                }
                                            }
                                        }}
                                        arrow
                                        placement="top"
                                    >
                                        <td style={{
                                            padding: '2px',
                                            textAlign: 'center',
                                            verticalAlign: 'middle',
                                        }}>
                                            <Box
                                                sx={{
                                                    backgroundColor: getBackgroundColor(value),
                                                    borderRadius: '2px',
                                                    color: getTextColor(value),
                                                    p: '6px 2px',
                                                    transition: 'all 0.15s ease-in-out',
                                                    cursor: 'pointer',
                                                    position: 'relative',
                                                    border: '1px solid transparent',
                                                    '&:hover': {
                                                        transform: 'scale(1.1)',
                                                        zIndex: 10,
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                                                        border: '1px solid white',
                                                        filter: 'brightness(1.2)',
                                                    }
                                                }}
                                            >
                                                <Typography variant="body2" fontWeight="600" sx={{ fontSize: '0.75rem' }}>
                                                    {value.toFixed(2)}
                                                </Typography>
                                                {/* Small indicator dot for changed cells */}
                                                {hasDiff && (
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        top: 2,
                                                        right: 2,
                                                        width: 4,
                                                        height: 4,
                                                        borderRadius: '50%',
                                                        backgroundColor: diff > 0 ? '#69f0ae' : '#ff5252',
                                                        boxShadow: '0 0 2px rgba(0,0,0,0.5)'
                                                    }} />
                                                )}
                                            </Box>
                                        </td>
                                    </Tooltip>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </Box>
    );
};

export default MatrixHeatmap;
